import { MutableRefObject, useEffect, useRef } from 'react';

const DURATION = 500;

type InitialStyle = { paddingTop: string; paddingBottom: string };

export default function useOpacity() {
    const ref = useRef<HTMLDivElement>(null);
    const animation = useRef<Animation>();
    const initialStyle = useRef<InitialStyle>({ paddingTop: '', paddingBottom: '' });
    if (ref.current) {
        const style = window.getComputedStyle(ref.current);
        initialStyle.current.paddingTop = style.paddingTop;
        initialStyle.current.paddingBottom = style.paddingBottom;
    }

    useEffect(() => {
        const cAnimation = animation.current;
        return () => {
            if (cAnimation) {
                cAnimation.cancel();
            }
        };
    }, []);

    async function hide() {
        await disolve(ref, animation, initialStyle);
    }
    async function reveal() {
        await apear(ref, animation);
    }

    return { hide, reveal, ref };
}

function apear(elRef: MutableRefObject<HTMLDivElement | null>, animation: MutableRefObject<Animation | undefined>) {
    return new Promise<void>((resolve) => {
        const el = elRef.current;
        if (el) {
            if (animation.current) {
                animation.current.cancel();
            }
            animation.current = el.animate(
                {
                    opacity: [0, 1],
                },
                { duration: DURATION, easing: 'ease-in-out' }
            );
            animation.current.onfinish = () => {
                animation.current = undefined;
                el.style.overflow = el.style.height = '';
                resolve();
            };
        } else {
            resolve();
        }
    });
}

function disolve(
    elRef: MutableRefObject<HTMLDivElement | null>,
    animation: MutableRefObject<Animation | undefined>,
    initialStyle: MutableRefObject<InitialStyle>
) {
    return new Promise<void>((resolve, reject) => {
        const el = elRef.current;
        if (el) {
            const startHeight = `${el.offsetHeight}px`;
            if (animation.current) {
                animation.current.cancel();
            }
            animation.current = el.animate(
                {
                    opacity: [1, 0],
                },
                { duration: DURATION, easing: 'ease-in-out', fill: 'forwards' }
            );
            animation.current.onfinish = () => {
                el.style.overflow = 'hidden';
                animation.current = el.animate(
                    {
                        height: [startHeight, `0px`],
                        paddingTop: [initialStyle.current.paddingTop, '0px'],
                        paddingBottom: [initialStyle.current.paddingBottom, '0px'],
                    },
                    { duration: DURATION, easing: 'ease-in-out', fill: 'forwards' }
                );
                animation.current.onfinish = () => {
                    animation.current = undefined;
                    el.style.overflow = '';
                    resolve();
                };
            };
        } else {
            reject();
        }
    });
}
